import { useState } from 'react'
import { DialogButton, DialogForm, DialogField, DialogHint, GOOGLE_ICON } from '..'
import VerlaufModalDialog from './VerlaufModalDialog'
import { useT } from '../../../i18n'

const MEDIA = { storageId: 101705, video: true }
const CODEPILOT_CONTACT = 'https://codepilot.arkturian.com/api/v1/contact/'
const EMAIL_KEY = 'tschepp-email'

interface VerlaufRegistrationDialogProps {
  open: boolean
  onClose: () => void
  onGoogleRegister: () => void
  onSwitchToLogin?: () => void
}

export default function VerlaufRegistrationDialog({ open, onClose, onGoogleRegister }: VerlaufRegistrationDialogProps) {
  const t = useT()
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

  const handleEmailRegister = async () => {
    if (!isValidEmail || submitting) return
    setSubmitting(true)

    // Save email locally
    localStorage.setItem(EMAIL_KEY, email)

    // Send beautifully designed welcome email
    await fetch(`${CODEPILOT_CONTACT}tschepp-welcome`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    }).catch(() => {})

    setSubmitting(false)
    setSubmitted(true)

    // Trigger survey after short delay
    setTimeout(() => {
      localStorage.setItem('tschepp-survey-pending', '1')
      onClose()
      // Force survey check by reloading — the survey effect in LandingPage
      // won't trigger since user is null for email-only registration.
      // Instead, we'll handle this via a custom event.
      window.dispatchEvent(new CustomEvent('tschepp-survey-ready'))
    }, 1500)
  }

  return (
    <VerlaufModalDialog open={open} onClose={onClose} theme="light" media={MEDIA}>
      <h3 className="sg-dialog__title">{t.dialog.register.title}</h3>
      <p className="sg-vdialog__desc sg-vdialog__desc--light">{t.dialog.register.description}</p>

      {!submitted ? (
        <>
          <DialogForm>
            <DialogField
              label={t.dialog.register.emailLabel}
              type="email"
              placeholder={t.dialog.register.emailPlaceholder}
              value={email}
              onChange={setEmail}
              light
            />
          </DialogForm>
          <DialogButton
            variant="primary"
            onClick={handleEmailRegister}
            disabled={!isValidEmail || submitting}
            style={{ marginBottom: 12 }}
          >
            {t.dialog.register.submit}
          </DialogButton>

          <div className="sg-dialog__divider">
            <span>{t.dialog.register.or}</span>
          </div>

          <DialogButton variant="google" icon={GOOGLE_ICON} onClick={() => { onClose(); onGoogleRegister() }}>
            {t.dialog.register.google}
          </DialogButton>
        </>
      ) : (
        <div className="sg-dialog__success">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="var(--ar-gold)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
            <path d="M22 4L12 14.01l-3-3" />
          </svg>
          <p>{t.dialog.register.emailSent}</p>
        </div>
      )}

      <DialogHint>{t.dialog.register.emailHint}</DialogHint>
    </VerlaufModalDialog>
  )
}
